import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  List,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageHeader } from '@/components/ui/page-header';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import {
  SelecteurSousSegmentFormation,
  useSousSegmentsFormation,
  type FormationSegmentValue,
} from '@/components/formation/SelecteurSousSegmentFormation';
import {
  exportToCSV,
  useBulkSessions,
  useFormateurs,
  useFormations,
  useSessions,
  useUpsertSession,
} from '@/hooks/queries/useFormation';
import { formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useRole } from '@/hooks/useRole';
import { t } from '@/locales/i18n';
import type { SessionFormationRow, SessionStatut } from '@/lib/database.types';
import { SessionFormDialog } from './SessionFormDialog';

type Vue = 'calendrier' | 'liste';

function startOfMonth(d: Date): Date {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function SessionsPage(): JSX.Element {
  const { isAdmin } = useRole();
  const [segment, setSegment] = useState<FormationSegmentValue>('all');
  const { ids: segmentIds } = useSousSegmentsFormation(segment);
  const [vue, setVue] = useState<Vue>('liste');
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [statut, setStatut] = useState<SessionStatut | ''>('');

  const { from, to, days } = useMemo(() => {
    const start = startOfMonth(cursor);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const list: Date[] = [];
    for (let d = new Date(start); d < end; d = new Date(d.getTime() + 86400000))
      list.push(new Date(d));
    return { from: start, to: end, days: list };
  }, [cursor]);

  const { data: formationsScoped = [] } = useFormations({ sousSegmentIds: segmentIds });
  const formationIdsScoped = useMemo(() => formationsScoped.map((f) => f.id), [formationsScoped]);

  const { data: allSessions = [] } = useSessions({
    statut: statut || null,
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  });
  const sessions = useMemo(
    () =>
      formationIdsScoped.length === 0
        ? allSessions
        : allSessions.filter((s) => formationIdsScoped.includes(s.formation_id)),
    [allSessions, formationIdsScoped],
  );

  const { data: formations = [] } = useFormations({});
  const { data: formateurs = [] } = useFormateurs();
  const upsert = useUpsertSession();
  const bulk = useBulkSessions();

  const formationMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of formations) m.set(f.id, f.titre);
    return m;
  }, [formations]);
  const formateurMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of formateurs) m.set(f.id, `${f.prenom} ${f.nom[0]}.`);
    return m;
  }, [formateurs]);

  const sessionsByDay = useMemo(() => {
    const m = new Map<string, typeof sessions>();
    for (const s of sessions) {
      const d = s.date_debut;
      if (!m.has(d)) m.set(d, []);
      m.get(d)!.push(s);
    }
    return m;
  }, [sessions]);

  const navigate = (delta: number): void => {
    const d = new Date(cursor);
    d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SessionFormationRow | null>(null);

  // Bulk
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const clearSelection = (): void => setSelected(new Set());
  const toggleSelect = (id: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = (): void => setSelected(new Set(sessions.map((s) => s.id)));
  const selectedRows = sessions.filter((s) => selected.has(s.id));

  const handleAnnule = async (s: SessionFormationRow): Promise<void> => {
    await upsert.mutateAsync({
      id: s.id,
      formation_id: s.formation_id,
      date_debut: s.date_debut,
      date_fin: s.date_fin,
      statut: 'annulee',
    });
    toast.success(`Session ${s.reference} annulée`);
  };

  const handleDuplicate = async (s: SessionFormationRow): Promise<void> => {
    const offset = 14;
    const nd = new Date(s.date_debut);
    nd.setDate(nd.getDate() + offset);
    const nf = new Date(s.date_fin);
    nf.setDate(nf.getDate() + offset);
    await upsert.mutateAsync({
      formation_id: s.formation_id,
      type: s.type,
      date_debut: nd.toISOString().slice(0, 10),
      date_fin: nf.toISOString().slice(0, 10),
      lieu_type: s.lieu_type,
      lieu_adresse: s.lieu_adresse,
      lien_visio: s.lien_visio,
      salle_id: s.salle_id,
      formateur_principal_id: s.formateur_principal_id,
      formateurs_co_animateurs_ids: s.formateurs_co_animateurs_ids,
      capacite_max: s.capacite_max,
      capacite_min: s.capacite_min,
      prix_session: s.prix_session,
      statut: 'planifiee',
    });
    toast.success('Session dupliquée 2 semaines après');
  };

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Sessions"
        subtitle={`${sessions.length} session(s) — ${cursor.toLocaleDateString('fr-DZ', { month: 'long', year: 'numeric' })}`}
        actions={
          <div className="flex items-center gap-2">
            <SelecteurSousSegmentFormation value={segment} onChange={setSegment} />
            <select
              value={statut}
              onChange={(e) => setStatut(e.target.value as SessionStatut | '')}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Tous statuts —</option>
              {(
                [
                  'planifiee',
                  'ouverte_inscriptions',
                  'complete',
                  'en_cours',
                  'terminee',
                  'annulee',
                ] as SessionStatut[]
              ).map((s) => (
                <option key={s} value={s}>
                  {t(`formation.session.statut.${s}`)}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1 rounded-md border border-input bg-background p-0.5">
              <button
                type="button"
                onClick={() => setVue('calendrier')}
                className={cn(
                  'rounded px-2 py-1 text-xs',
                  vue === 'calendrier'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground',
                )}
              >
                <Calendar className="inline h-3 w-3" /> Calendrier
              </button>
              <button
                type="button"
                onClick={() => setVue('liste')}
                className={cn(
                  'rounded px-2 py-1 text-xs',
                  vue === 'liste' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                )}
              >
                <List className="inline h-3 w-3" /> Liste
              </button>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(startOfMonth(new Date()))}>
              Aujourd&apos;hui
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(1)}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" /> Nouvelle session
            </Button>
          </div>
        }
      />

      <BulkActionBar count={selected.size} onClear={clearSelection}>
        <BulkButton onClick={selectAll}>Tout ({sessions.length})</BulkButton>
        <BulkButton
          onClick={async () => {
            const r = await bulk.mutateAsync({ ids: [...selected], action: 'annuler' });
            toast.success(`${r.success} session(s) annulée(s)`);
            clearSelection();
          }}
        >
          <XCircle className="h-3.5 w-3.5" /> Annuler
        </BulkButton>
        <BulkButton
          onClick={async () => {
            const r = await bulk.mutateAsync({
              ids: [...selected],
              action: 'reporter',
              decalage_jours: 7,
            });
            toast.success(`${r.success} session(s) reportée(s) (+7j)`);
            clearSelection();
          }}
        >
          <ChevronRight className="h-3.5 w-3.5" /> Reporter +7j
        </BulkButton>
        <BulkButton
          onClick={async () => {
            const r = await bulk.mutateAsync({ ids: [...selected], action: 'cloturer' });
            toast.success(`${r.success} session(s) clôturée(s)`);
            clearSelection();
          }}
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> Clôturer
        </BulkButton>
        <BulkButton
          onClick={() => {
            exportToCSV(
              selectedRows.map((s) => ({
                reference: s.reference,
                formation: formationMap.get(s.formation_id) ?? '',
                type: s.type,
                date_debut: s.date_debut,
                date_fin: s.date_fin,
                statut: s.statut,
                capacite_max: s.capacite_max,
                prix_session: s.prix_session ?? '',
              })),
              `sessions-${selectedRows.length}.csv`,
            );
            toast.success(`${selectedRows.length} ligne(s) exportée(s)`);
          }}
        >
          <Download className="h-3.5 w-3.5" /> Export CSV
        </BulkButton>
        {isAdmin && (
          <BulkButton variant="destructive" onClick={() => setBulkConfirmDelete(true)}>
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </BulkButton>
        )}
      </BulkActionBar>

      {vue === 'calendrier' ? (
        <div className="grid grid-cols-7 gap-1">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
            <div
              key={d}
              className="px-2 py-1 text-center text-[10px] font-semibold uppercase text-muted-foreground"
            >
              {d}
            </div>
          ))}
          {Array.from({ length: ((days[0]?.getDay() ?? 1) + 6) % 7 }, (_, i) => (
            <div key={`pad-${i}`} className="min-h-[100px]" />
          ))}
          {days.map((d) => {
            const key = d.toISOString().slice(0, 10);
            const dayEvents = sessionsByDay.get(key) ?? [];
            const isToday = key === new Date().toISOString().slice(0, 10);
            return (
              <div
                key={key}
                className={cn(
                  'min-h-[100px] rounded-md border bg-card p-1.5',
                  isToday ? 'border-primary' : 'border-border',
                )}
              >
                <div className="text-[10px] text-muted-foreground">{d.getDate()}</div>
                <ul className="mt-1 space-y-0.5">
                  {dayEvents.slice(0, 3).map((s) => (
                    <li key={s.id}>
                      <Link
                        to={`/formation/sessions/${s.id}`}
                        className={cn(
                          'block truncate rounded border px-1 py-0.5 text-[10px]',
                          s.statut === 'terminee'
                            ? 'border-success/40 bg-success/10'
                            : 'border-primary/40 bg-primary/5',
                        )}
                        title={`${formationMap.get(s.formation_id) ?? ''} — ${t(`formation.session.statut.${s.statut}`)}`}
                      >
                        {formationMap.get(s.formation_id) ?? s.reference}
                      </Link>
                    </li>
                  ))}
                  {dayEvents.length > 3 && (
                    <li className="text-[10px] text-muted-foreground">+{dayEvents.length - 3}</li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-muted/40 text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={selected.size === sessions.length && sessions.length > 0}
                    onChange={() =>
                      selected.size === sessions.length ? clearSelection() : selectAll()
                    }
                  />
                </th>
                <th className="px-3 py-2 text-left">Réf</th>
                <th className="px-3 py-2 text-left">Formation</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Période</th>
                <th className="px-3 py-2 text-left">Formateur</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr
                  key={s.id}
                  className={cn(
                    'border-b border-border/40 hover:bg-muted/20',
                    selected.has(s.id) && 'bg-primary/5',
                  )}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(s.id)}
                      onChange={() => toggleSelect(s.id)}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono">
                    <Link
                      to={`/formation/sessions/${s.id}`}
                      className="text-primary hover:underline"
                    >
                      {s.reference}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{formationMap.get(s.formation_id) ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        s.type === 'intra'
                          ? 'inline-flex items-center rounded-md border border-violet-300 bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-800'
                          : s.type === 'inter'
                            ? 'inline-flex items-center rounded-md border border-indigo-300 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-800'
                            : 'inline-flex items-center rounded-md border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-700'
                      }
                    >
                      {t(`formation.session.type.${s.type}`)}
                    </span>
                  </td>
                  <td className="num px-3 py-2">
                    {formatDateDZ(s.date_debut)} → {formatDateDZ(s.date_fin)}
                  </td>
                  <td className="px-3 py-2">
                    {s.formateur_principal_id ? formateurMap.get(s.formateur_principal_id) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline">{t(`formation.session.statut.${s.statut}`)}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onSelect={() => {
                            setEditing(s);
                            setDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" /> Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => void handleDuplicate(s)}>
                          <Copy className="h-3.5 w-3.5" /> Dupliquer (+2 sem.)
                        </DropdownMenuItem>
                        {s.statut !== 'annulee' && (
                          <DropdownMenuItem
                            onSelect={() => void handleAnnule(s)}
                            className="text-destructive"
                          >
                            <XCircle className="h-3.5 w-3.5" /> Annuler
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-4 text-center text-muted-foreground">
                    Aucune session ce mois.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <SessionFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultSousSegmentIds={segmentIds.length > 0 ? segmentIds : undefined}
        editing={editing}
        onSaved={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />

      <DangerousConfirmDialog
        open={bulkConfirmDelete}
        onOpenChange={setBulkConfirmDelete}
        title={`Supprimer ${selected.size} session${selected.size > 1 ? 's' : ''} ?`}
        description="Action irréversible. Les sessions avec inscriptions ne seront pas supprimées (échec gracieux)."
        consequences={[
          `${selected.size} session(s) effacées`,
          'Les inscriptions / présences / évaluations bloquent la suppression',
          'Audit log conservé',
        ]}
        challenge="SUPPRIMER"
        loading={bulk.isPending}
        onConfirm={async () => {
          const r = await bulk.mutateAsync({ ids: [...selected], action: 'delete' });
          if (r.errors.length === 0) toast.success(`${r.success} session(s) supprimée(s)`);
          else
            toast.warning(`${r.success} OK · ${r.errors.length} échec(s) — ${r.errors[0] ?? ''}`);
          setBulkConfirmDelete(false);
          clearSelection();
        }}
      />
    </div>
  );
}
